import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { ClientLayout } from "../components/ClientLayout";
import { clientApi } from "../services/clientApi.service";
import { allergenService } from "@/services/allergen.service";
import type { Allergen } from "@/types";

/**
 * Page de déclaration des allérgènes du client (chips sélectionnables).
 *
 * Les allérgènes déclarés peuvent être utilisés en V1.1 pour prévenir l'utilisateur
 * quand un plat contient un allérgène déclaré.
 */
export function ClientAllergensPage(): JSX.Element {
  const navigate = useNavigate();
  const [allergens, setAllergens] = useState<Allergen[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [initialIds, setInitialIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const [profile, all] = await Promise.all([
          clientApi.getMe(),
          allergenService.getAll(),
        ]);
        if (cancelled) return;
        const list = Array.isArray(all) ? all : ((all as { data?: Allergen[] }).data ?? []);
        setAllergens(list);
        const ids = new Set(profile.customer.allergens.map((a) => a.id));
        setSelectedIds(ids);
        setInitialIds(new Set(ids));
      } catch (err) {
        if (cancelled) return;
        const message =
          (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
          "Impossible de charger les allérgènes.";
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  function toggle(id: string): void {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const dirty = useMemo(() => {
    if (selectedIds.size !== initialIds.size) return true;
    for (const id of selectedIds) if (!initialIds.has(id)) return true;
    return false;
  }, [selectedIds, initialIds]);

  async function handleSave(): Promise<void> {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await clientApi.updateMe({ allergenIds: Array.from(selectedIds) });
      setInitialIds(new Set(selectedIds));
      setSuccess("Vos allérgènes ont été mis à jour.");
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        "Impossible d'enregistrer.";
      setError(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <ClientLayout title="Mes allérgènes">
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate(-1)} aria-label="Retour">
            <ArrowBackRoundedIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Mes allérgènes
          </Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          Sélectionnez les allérgènes qui vous concernent pour être alerté quand un plat
          en contient.
        </Typography>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {success ? <Alert severity="success">{success}</Alert> : null}

        <Paper sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {allergens.map((a) => {
                const selected = selectedIds.has(a.id);
                return (
                  <Chip
                    key={a.id}
                    label={a.name}
                    color={selected ? "secondary" : "default"}
                    variant={selected ? "filled" : "outlined"}
                    onClick={() => toggle(a.id)}
                    icon={a.icon ? <span aria-hidden>{a.icon}</span> : undefined}
                  />
                );
              })}
            </Box>
          )}
        </Paper>

        <Button
          variant="contained"
          color="secondary"
          onClick={handleSave}
          disabled={!dirty || saving}
        >
          {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </Stack>
    </ClientLayout>
  );
}
